import { Table } from "dexie";
import { action, makeObservable, observable, override } from "mobx";

import { AppSubStoreArgs } from "@/store/types";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { OptimisticSyncUpdate, SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { AudioRecordingObservable } from "./audio-recording-observable";
import { AudioRecordingModelData, IndexedAudioRecordingSyncUpdateValue } from "./types";
import { AudioRecordingIndexes } from "@/store/audio-recordings/audio-recording-indexes";
import { Maybe } from "@/domains/common/types";
import { AppStore } from "@/store/AppStore";
import { Uuid } from "@/domains/global/identifiers";
import { NoteAudioRecordingObservable } from "@/store/audio-recordings/note-audio-recording-observable";
import { clientEnvModule } from "@/modules/client-env";

export class AppStoreAudioRecordingStore extends BaseSyncModelStore<
  AudioRecordingObservable,
  AudioRecordingModelData
> {
  private noteRecordingObservables = new Map<Uuid, NoteAudioRecordingObservable>();
  private localClientGroupIdPromise: Promise<Uuid> | null = null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.AudioRecording, ...injectedDeps });

    makeObservable<
      AppStoreAudioRecordingStore,
      | "noteRecordingObservables"
      | "getOrCreateNoteRecordingObservable"
      | "localClientGroupIdPromise"
    >(this, {
      createSyncModel: true,
      remoteTable: override,
      localTable: override,
      computeIndexes: override,
      resetSync: override,
      getNoteRecording: action,
      noteRecordingObservables: observable,
      getOrCreateNoteRecordingObservable: action,
      localClientGroupIdPromise: observable,
      getLocalClientGroupId: action,
    });
  }

  createSyncModel(data: SyncUpdateValue<AudioRecordingModelData>): AudioRecordingObservable {
    return new AudioRecordingObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public get remoteTable() {
    return this.db.mappedTables[this.modelKind].remote as Table<
      SyncUpdateValue<AudioRecordingModelData>
    >;
  }

  public get localTable() {
    return this.db.mappedTables[this.modelKind]
      .local as Table<IndexedAudioRecordingSyncUpdateValue>;
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<AudioRecordingModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<AudioRecordingModelData>[];
  }): Record<string, unknown> {
    return new AudioRecordingIndexes({ remoteData, optimisticUpdates }).indexes;
  }

  getNoteRecording({ noteId }: { noteId: Uuid | null }): NoteAudioRecordingObservable {
    if (!noteId) {
      return new NoteAudioRecordingObservable({ store: this, noteId: null });
    }

    return this.getOrCreateNoteRecordingObservable(noteId);
  }

  override resetSync(): void {
    super.resetSync();
    this.noteRecordingObservables.forEach(observable => observable.dispose());
    this.noteRecordingObservables.clear();
  }

  private getOrCreateNoteRecordingObservable(noteId: Uuid): NoteAudioRecordingObservable {
    let observable = this.noteRecordingObservables.get(noteId);

    if (!observable) {
      observable = new NoteAudioRecordingObservable({ store: this, noteId });
      this.noteRecordingObservables.set(noteId, observable);
    }

    return observable;
  }

  getLocalClientGroupId(): Promise<Uuid> {
    if (!this.localClientGroupIdPromise) {
      this.localClientGroupIdPromise = clientEnvModule
        .clientGroupId()
        .then(({ clientGroupId }) => clientGroupId);
    }

    return this.localClientGroupIdPromise;
  }
}
